import { Box, IconButton } from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import { useEffect, useState } from "react";

const SCROLL_AMOUNT = 200;
// Clickable icons on the inverted filter bar read the inverted accent.
const ARROW_SX = {
  color: "text.accentInverted",
  "&.MuiIconButton-root.Mui-disabled": { color: "filter.textDisabled" },
} as const;

interface Size {
  scrollWidth: number;
  clientWidth: number;
}

/**
 * Width of the scrolled row, tracked through a callback ref so the observer
 * attaches as soon as the node is there.
 *
 * Everything the arrows need is derived from this one state: arrows change
 * the row's available width, so keeping their visibility in a state written
 * from an effect fed the measurement back into itself, one layout hair away
 * from an update loop.
 */
const useRowSize = (): [
  (node: HTMLDivElement | null) => void,
  HTMLDivElement | null,
  Size | null
] => {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [size, setSize] = useState<Size | null>(null);

  useEffect(() => {
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { scrollWidth, clientWidth } = entry.target;
      setSize((previous) =>
        previous?.scrollWidth === scrollWidth &&
        previous?.clientWidth === clientWidth
          ? previous
          : { scrollWidth, clientWidth }
      );
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [setNode, node, size];
};

const Expand = ({ children }: { children: JSX.Element }) => {
  const [currentScroll, setCurrentScroll] = useState(0);
  const [attachRow, scroller, size] = useRowSize();

  const overflows = !!size && size.scrollWidth > size.clientWidth;
  const disableArrow = {
    left: currentScroll <= 0,
    right: !size || size.clientWidth + currentScroll >= size.scrollWidth,
  };

  useEffect(() => {
    if (!overflows && currentScroll !== 0) {
      setCurrentScroll(0);
    }
  }, [overflows, currentScroll]);

  return (
    <>
      {overflows && (
        <IconButton
          onClick={() => {
            if (size) {
              const futurScrollPos = currentScroll - SCROLL_AMOUNT;

              const scrollToCalcul =
                futurScrollPos < 0 ? 0 : currentScroll - SCROLL_AMOUNT;

              scroller?.scrollTo({
                left: scrollToCalcul,
                top: 0,
                behavior: "smooth",
              });
              setCurrentScroll(scrollToCalcul);
            }
          }}
          disabled={disableArrow.left}
          size="large"
          sx={ARROW_SX}
        >
          <ArrowLeftIcon />
        </IconButton>
      )}

      <Box
        ref={attachRow}
        sx={{
          overflow: "hidden",
          alignItems: "center",
          gap: 1,
          display: "flex",
        }}
      >
        {children}
      </Box>
      {overflows && (
        <IconButton
          onClick={() => {
            if (size) {
              const futurScrollPos =
                currentScroll + size.clientWidth + SCROLL_AMOUNT;

              const scrollToCalcul =
                futurScrollPos > size.scrollWidth
                  ? size.scrollWidth - size.clientWidth
                  : currentScroll + SCROLL_AMOUNT;

              scroller?.scrollTo({
                left: scrollToCalcul,
                top: 0,
                behavior: "smooth",
              });
              setCurrentScroll(scrollToCalcul);
            }
          }}
          disabled={disableArrow.right}
          size="large"
          sx={ARROW_SX}
        >
          <ArrowRightIcon />
        </IconButton>
      )}
    </>
  );
};

export default Expand;
